import React, { useMemo } from 'react'
import { Line } from 'react-chartjs-2'
import { format } from 'date-fns'
import zoomPlugin from 'chartjs-plugin-zoom'

interface InfrastructureObject {
  id: number
  name: string
  infrastructure_type: string
}

interface LiveReading {
  id: number
  object_id: number
  object_name: string
  infrastructure_type: string
  reading_type: string
  reading_type_label: string
  value: number
  timestamp: string
}

interface Anomaly extends LiveReading {
  deviation: number
  trend: 'above' | 'below'
}

interface LiveChartProps {
  object: InfrastructureObject
  readings: LiveReading[]
  anomalies: Anomaly[]
  startTime: Date
  endTime: Date
}

export default function LiveChart({ object, readings, anomalies, startTime, endTime }: LiveChartProps) {
  const [viewMode, setViewMode] = React.useState<'values' | 'flow'>('values')
  
  // Calculate flow (difference between readings 10 minutes apart)
  const flowData = useMemo(() => {
    const flowByType: { [key: string]: Array<{timestamp: string, flow: number, prevValue: number, currentValue: number}> } = {}

    // Group by reading type first
    const grouped = readings.reduce((acc, reading) => {
      if (!acc[reading.reading_type]) {
        acc[reading.reading_type] = []
      }
      acc[reading.reading_type].push(reading)
      return acc
    }, {} as { [key: string]: LiveReading[] })

    // Calculate flow for each type using 10-minute intervals
    Object.keys(grouped).forEach(type => {
      const typeReadings = grouped[type].sort((a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      )

      flowByType[type] = []

      // Use readings approximately 10 minutes apart
      const minIntervalMs = 8 * 60 * 1000  // 8 minutes
      const maxIntervalMs = 12 * 60 * 1000 // 12 minutes

      for (let i = 1; i < typeReadings.length; i++) {
        const currentReading = typeReadings[i]
        const currentTime = new Date(currentReading.timestamp).getTime()

        // Find the most recent reading within 8-12 minutes before this one
        let prevIndex = -1
        for (let j = i - 1; j >= 0; j--) {
          const prevTime = new Date(typeReadings[j].timestamp).getTime()
          const timeDiff = currentTime - prevTime

          if (timeDiff >= minIntervalMs && timeDiff <= maxIntervalMs) {
            prevIndex = j
            break // Take the most recent one that fits
          } else if (timeDiff > maxIntervalMs) {
            break // Too far back, stop searching
          }
        }

        if (prevIndex !== -1) {
          const prevReading = typeReadings[prevIndex]

          const timeDiff = (currentTime - new Date(prevReading.timestamp).getTime()) / (1000 * 60 * 60) // hours
          const valueDiff = currentReading.value - prevReading.value

          // Calculate flow rate - values are in liters, convert to m³/h
          const flow = timeDiff > 0 ? (valueDiff / 1000) / timeDiff : 0

          // Only include positive flow (consumption) or very small negatives (measurement errors)
          if (flow >= -0.0001) { // Allow small measurement errors (adjusted for m³)
            flowByType[type].push({
              timestamp: currentReading.timestamp,
              flow: Math.max(0, flow), // Clamp to 0 minimum
              prevValue: prevReading.value,
              currentValue: currentReading.value
            })
          }
        }
      }
    })

    return flowByType
  }, [readings])

  // Group readings by reading type
  const readingsByType = useMemo(() => {
    return readings.reduce((acc, reading) => {
      if (!acc[reading.reading_type]) {
        acc[reading.reading_type] = []
      }
      acc[reading.reading_type].push(reading)
      return acc
    }, {} as { [key: string]: LiveReading[] })
  }, [readings])

  // Create chart data (either values or flow based on viewMode)
  const chartData = useMemo(() => {
    // Sort reading types for consistent color assignment
    const sortedTypes = Object.keys(readingsByType).sort()

    const datasets = []

    // Color scheme
    const colors = [
      'rgb(147, 51, 234)', // Purple
      'rgb(59, 130, 246)', // Blue
      'rgb(16, 185, 129)', // Emerald
    ]

    let colorIndex = 0

    for (const readingType of sortedTypes) {
      if (viewMode === 'values') {
        // Show actual values
        const typeReadings = readingsByType[readingType]
          .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())

        datasets.push({
          label: typeReadings[0]?.reading_type_label || readingType,
          data: typeReadings.map(reading => ({
            x: new Date(reading.timestamp),
            y: reading.value / 1000, // Convert liters to m³
          })),
          borderColor: colors[colorIndex % colors.length],
          backgroundColor: colors[colorIndex % colors.length].replace('rgb', 'rgba').replace(')', ', 0.1)'),
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 4,
          tension: 0.1,
        })
      } else {
        // Show flow rate
        const typeFlow = flowData[readingType] || []
        
        if (typeFlow.length > 0) {
          datasets.push({
            label: `${typeFlow[0] ? readingsByType[readingType][0]?.reading_type_label : readingType} (przepływ)`,
            data: typeFlow.map(flow => ({
              x: new Date(flow.timestamp),
              y: flow.flow,
            })),
            borderColor: colors[colorIndex % colors.length],
            backgroundColor: colors[colorIndex % colors.length].replace('rgb', 'rgba').replace(')', ', 0.1)'),
            borderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            tension: 0.1,
            fill: true,
          })
        }
      }

      colorIndex++
    }

    // Add anomaly markers (only in values mode)
    if (anomalies.length > 0 && viewMode === 'values') {
      datasets.push({
        label: '⚠️ Anomalie',
        data: anomalies.map(anomaly => ({
          x: new Date(anomaly.timestamp),
          y: anomaly.value / 1000, // Convert liters to m³
        })),
        backgroundColor: 'rgb(239, 68, 68)', // Red
        borderColor: 'rgb(239, 68, 68)',
        borderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
        showLine: false,
      })
    }

    return {
      datasets,
    }
  }, [readingsByType, anomalies, viewMode, flowData])

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          pointStyle: 'line',
        },
      },
      tooltip: {
        callbacks: {
          title: function(context: any) {
            return format(new Date(context[0].parsed.x), 'dd.MM.yyyy HH:mm:ss')
          },
          label: function(context: any) {
            let label = context.dataset.label || ''
            if (label) {
              label += ': '
            }

            if (viewMode === 'values') {
              label += context.parsed.y.toLocaleString('pl-PL', { maximumFractionDigits: 2 }) + ' m³'

              // Add anomaly info if this is an anomaly point
              const isAnomaly = context.dataset.label?.includes('⚠️ Anomalie')
              if (isAnomaly) {
                const anomaly = anomalies.find(a =>
                  Math.abs(new Date(a.timestamp).getTime() - context.parsed.x) < 1000 &&
                  (a.value / 1000) === context.parsed.y
                )
                if (anomaly) {
                  label += ` (${anomaly.deviation.toFixed(1)}% ${anomaly.trend === 'above' ? 'powyżej' : 'poniżej'} średniej)`
                }
              }
            } else {
              // Flow mode
              label += context.parsed.y.toLocaleString('pl-PL', { maximumFractionDigits: 2 }) + ' m³/h'
            }

            return label
          },
        },
      },
      zoom: {
        zoom: {
          wheel: {
            enabled: true,
          },
          drag: {
            enabled: true,
          },
          mode: 'x' as const,
        },
        pan: {
          enabled: true,
          mode: 'x' as const,
        },
      },
    },
    scales: {
      x: {
        type: 'time' as const,
        time: {
          unit: 'hour' as const,
          displayFormats: {
            hour: 'dd.MM HH:mm',
            day: 'dd.MM.yyyy',
          },
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          maxTicksLimit: 12,
        },
      },
      y: {
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          callback: function(value: any) {
            if (viewMode === 'values') {
              return value.toLocaleString('pl-PL', { maximumFractionDigits: 2 }) + ' m³'
            } else {
              return value.toLocaleString('pl-PL', { maximumFractionDigits: 2 }) + ' m³/h'
            }
          },
        },
        title: {
          display: true,
          text: viewMode === 'values' ? 'Wartość [m³]' : 'Przepływ [m³/h]',
        },
      },
    },
    elements: {
      point: {
        borderWidth: 1,
      },
    },
  }), [anomalies, viewMode])

  // Statistics for flow mode
  const flowStats = useMemo(() => {
    if (viewMode !== 'flow') return null
    
    const allFlows: number[] = []
    Object.values(flowData).forEach(flows => {
      flows.forEach(f => allFlows.push(f.flow))
    })
    
    if (allFlows.length === 0) return null
    
    const min = Math.min(...allFlows)
    const max = Math.max(...allFlows)
    const avg = allFlows.reduce((a, b) => a + b, 0) / allFlows.length
    const total = allFlows.reduce((a, b) => a + b, 0)
    
    return { min, max, avg, total, count: allFlows.length }
  }, [flowData, viewMode])

  // Statistics - convert liters to m³
  const stats = useMemo(() => {
    if (readings.length === 0) return null

    const values = readings.map(r => r.value / 1000) // Convert to m³
    const min = Math.min(...values)
    const max = Math.max(...values)
    const avg = values.reduce((a, b) => a + b, 0) / values.length

    // Calculate time span
    const timestamps = readings.map(r => new Date(r.timestamp).getTime())
    const timeSpan = (Math.max(...timestamps) - Math.min(...timestamps)) / (1000 * 60 * 60) // hours

    // Calculate rate of change (slope)
    const sortedReadings = [...readings].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
    let trend = 'stabilny'
    if (sortedReadings.length >= 2) {
      const firstValue = sortedReadings[0].value
      const lastValue = sortedReadings[sortedReadings.length - 1].value
      const change = (lastValue - firstValue) / firstValue
      if (change > 0.01) trend = 'rosnący'
      else if (change < -0.01) trend = 'malejący'
    }

    return { min, max, avg, count: values.length, timeSpan, trend }
  }, [readings])

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            {object.name}
          </h3>
          <p className="text-sm text-gray-600 mb-2">{object.infrastructure_type}</p>
          <p className="text-xs text-gray-500">
            {format(startTime, 'dd.MM.yyyy HH:mm')} - {format(endTime, 'dd.MM.yyyy HH:mm')}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-600">
            {anomalies.length > 0 ? (
              <span className="text-red-600 font-medium">
                ⚠️ {anomalies.length} anomalii(anomalia)
              </span>
            ) : (
              '✓ Dane stabilne'
            )}
          </div>
          
          {/* View mode toggle */}
          <div className="flex border border-gray-300 rounded-md overflow-hidden">
            <button
              onClick={() => setViewMode('values')}
              className={`px-3 py-1 text-sm font-medium transition-colors ${
                viewMode === 'values'
                  ? 'bg-purple-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              📊 Wartości
            </button>
            <button
              onClick={() => setViewMode('flow')}
              className={`px-3 py-1 text-sm font-medium transition-colors ${
                viewMode === 'flow'
                  ? 'bg-purple-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              💧 Przepływ
            </button>
          </div>
        </div>
      </div>

      {readings.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-center text-gray-500">
            <div className="text-lg mb-2">📊 Brak danych</div>
            <div className="text-sm">
              W wybranym zakresie czasowym nie znaleziono danych live dla tego obiektu
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Chart */}
          <div className="mb-6">
            <div style={{ height: '400px' }}>
              <Line data={chartData} options={chartOptions} />
            </div>
          </div>

          {/* Statistics */}
          {viewMode === 'values' && stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 bg-gray-50 p-4 rounded-lg">
              <div className="text-center">
                <div className="text-sm font-medium text-gray-600">Minimum</div>
                <div className="text-lg font-bold text-blue-600">
                  {stats.min.toLocaleString('pl-PL', { maximumFractionDigits: 2 })} m³
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm font-medium text-gray-600">Średnia</div>
                <div className="text-lg font-bold text-purple-600">
                  {stats.avg.toLocaleString('pl-PL', { maximumFractionDigits: 2 })} m³
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm font-medium text-gray-600">Maksimum</div>
                <div className="text-lg font-bold text-green-600">
                  {stats.max.toLocaleString('pl-PL', { maximumFractionDigits: 2 })} m³
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm font-medium text-gray-600">Liczba odczytów</div>
                <div className="text-lg font-bold text-orange-600">
                  {stats.count}
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm font-medium text-gray-600">Zakres czasu</div>
                <div className="text-lg font-bold text-indigo-600">
                  {stats.timeSpan.toFixed(1)} h
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm font-medium text-gray-600">Trend</div>
                <div className={`text-sm font-bold ${
                  stats.trend === 'rosnący' ? 'text-red-600' :
                  stats.trend === 'malejący' ? 'text-blue-600' :
                  'text-green-600'
                }`}>
                  {stats.trend === 'rosnący' ? '↗ Rosnący' :
                   stats.trend === 'malejący' ? '↘ Malejący' :
                   '→ Stabilny'}
                </div>
              </div>
            </div>
          )}

          {/* Flow Statistics */}
          {viewMode === 'flow' && flowStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 bg-gray-50 p-4 rounded-lg">
              <div className="text-center">
                <div className="text-sm font-medium text-gray-600">Min przepływ</div>
                <div className="text-lg font-bold text-blue-600">
                  {flowStats.min.toLocaleString('pl-PL', { maximumFractionDigits: 2 })} m³/h
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm font-medium text-gray-600">Średni przepływ</div>
                <div className="text-lg font-bold text-purple-600">
                  {flowStats.avg.toLocaleString('pl-PL', { maximumFractionDigits: 2 })} m³/h
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm font-medium text-gray-600">Max przepływ</div>
                <div className="text-lg font-bold text-green-600">
                  {flowStats.max.toLocaleString('pl-PL', { maximumFractionDigits: 2 })} m³/h
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm font-medium text-gray-600">Całkowite zużycie</div>
                <div className="text-lg font-bold text-orange-600">
                  {flowStats.total.toLocaleString('pl-PL', { maximumFractionDigits: 2 })} m³
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm font-medium text-gray-600">Liczba pomiarów</div>
                <div className="text-lg font-bold text-indigo-600">
                  {flowStats.count}
                </div>
              </div>
            </div>
          )}

          {/* Anomaly details if any */}
          {anomalies.length > 0 && (
            <div className="mt-6 border-t pt-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">⚠️ Szczegóły anomalii</h4>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {anomalies.slice(0, 10).map((anomaly, index) => (
                  <div key={index} className="text-xs bg-red-50 p-2 rounded flex justify-between items-center">
                    <div>
                      <span className="font-medium">{anomaly.reading_type_label}</span>:
                      {(anomaly.value / 1000).toLocaleString('pl-PL', { maximumFractionDigits: 2 })} m³
                    </div>
                    <div className="text-red-600 font-medium">
                      {anomaly.deviation.toFixed(1)}% {anomaly.trend === 'above' ? '↑' : '↓'}
                    </div>
                    <div className="text-gray-500 text-xs">
                      {format(new Date(anomaly.timestamp), 'HH:mm')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
